/**
 * Centralized authenticated fetch with 401 retry-after-refresh.
 *
 * Every service should use `fetchWithAuth` instead of raw `fetch` for
 * authenticated requests. On a 401 response, this module will:
 *   1. Attempt a single token refresh via tokenManager
 *   2. Retry the original request with the new token
 *   3. Only force logout if the token is confirmed dead (via /verify-token/)
 *
 * Concurrent 401s share the same refresh promise (no stampede).
 */

import tokenManager from './tokenManager'
import { notifyHostAuthInvalid } from './embedAuthSignal.mjs'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'https://api.dale365.com/api/v1'

// Shared refresh promise — prevents concurrent refresh calls
let refreshPromise = null

// Refresh-wait timeout. Bumped from 10s to 30s — mobile networks on
// reconnect can easily exceed 10s for a single POST + DB write.
const REFRESH_WAIT_TIMEOUT_MS = 30_000

/**
 * Attempt to refresh the token. Concurrent callers share the same promise.
 * If tokenManager is already refreshing (e.g. from its auto-refresh timer),
 * we wait for the current token in localStorage to update rather than
 * starting a competing refresh that would be rejected by the isRefreshing guard.
 * @returns {Promise<string|null>} The new token, or null if refresh failed.
 */
function ensureTokenRefresh() {
  if (refreshPromise) {
    return refreshPromise
  }

  // If tokenManager is already mid-refresh (from its timer), wait for it
  // rather than starting a new refresh that would be rejected
  if (tokenManager.isRefreshing) {
    refreshPromise = new Promise((resolve) => {
      const onRefreshed = (event) => {
        window.removeEventListener('tokenRefreshed', onRefreshed)
        clearTimeout(timeout)
        resolve(event.detail?.newToken || localStorage.getItem('userToken'))
      }
      const timeout = setTimeout(() => {
        window.removeEventListener('tokenRefreshed', onRefreshed)
        // Fallback: if the timer expires but a token is present in localStorage,
        // use it rather than declaring failure. The concurrent refresh may have
        // completed without firing the event in our window.
        resolve(localStorage.getItem('userToken') || null)
      }, REFRESH_WAIT_TIMEOUT_MS)
      window.addEventListener('tokenRefreshed', onRefreshed)
    }).finally(() => {
      refreshPromise = null
    })
    return refreshPromise
  }

  refreshPromise = tokenManager.refreshToken()
    .then((success) => {
      if (success) {
        return localStorage.getItem('userToken')
      }
      return null
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

/**
 * Ask the backend whether the current token is genuinely dead. Only a
 * definitive 401 + invalidated response is treated as "dead". Anything else
 * (network error, 5xx, CORS hiccup) is treated as "indeterminate" and
 * leaves the session alone.
 * @returns {Promise<boolean>} true if the token is confirmed invalid.
 */
async function isTokenConfirmedDead() {
  const token = localStorage.getItem('userToken')
  if (!token) return true
  try {
    const res = await fetch(`${API_BASE_URL}/verify-token/`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Token ${token}`
      }
    })
    if (res.status === 401) return true
    if (res.ok) {
      const data = await res.json().catch(() => null)
      return !!(data && data.valid === false)
    }
    return false
  } catch (_e) {
    return false
  }
}

/**
 * Perform a fetch that automatically retries once on 401 after refreshing
 * the auth token.
 *
 * @param {string} url  Full URL to fetch
 * @param {object} config  Standard fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} The fetch Response object
 */
export async function fetchWithAuth(url, config = {}) {
  const response = await fetch(url, config)

  if (response.status !== 401) {
    return response
  }

  // 401 received — try to refresh the token and retry once
  const newToken = await ensureTokenRefresh()

  if (!newToken) {
    // Refresh path did not yield a token. Verify with the backend before
    // destroying the session — the 401 may have been a permission error or
    // a backend hiccup, not an actual invalid token.
    if (await isTokenConfirmedDead()) {
      forceLogout()
    }
    return response
  }

  // Rebuild headers with the fresh token
  const retryHeaders = { ...config.headers }
  retryHeaders['Authorization'] = `Token ${newToken}`

  const retryConfig = { ...config, headers: retryHeaders }
  const retryResponse = await fetch(url, retryConfig)

  // If the retry also returns 401, confirm the token is dead before wiping
  // the session. Endpoints can legitimately return 401 for reasons unrelated
  // to token validity (permission changes, rate limits, etc.).
  if (retryResponse.status === 401) {
    if (await isTokenConfirmedDead()) {
      forceLogout()
    }
  }

  return retryResponse
}

/**
 * Force logout — clear auth state and redirect to login.
 * Only called when token is confirmed invalid by /verify-token/.
 */
function forceLogout() {
  localStorage.removeItem('userToken')
  localStorage.setItem('userLoggedIn', 'false')

  // Stop token manager auto-refresh
  tokenManager.stop()

  // Dentro do Dale Desktop quem decide o proximo passo e o host: ele abre a
  // tela de autorizacao dele. Navegar aqui trocaria o painel pelo formulario
  // de login DENTRO da sidebar, onde o usuario nao tem o que fazer - a sessao
  // dele e do aplicativo, nao daquele iframe (#234).
  if (notifyHostAuthInvalid()) return

  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login'
  }
}

export default fetchWithAuth
